import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, Layers } from "lucide-react";
import { API } from "../../context/AuthContext";
import NewTemplateForm from "./NewTemplateForm";

const inp = "bg-gray-950 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-200 focus:outline-none focus:border-[#f59e0b]/50";

export default function TemplateManager() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadList = () => axios.get(`${API}/templates`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { loadList(); }, []);
  useEffect(() => {
    if (!sel) return;
    axios.get(`${API}/templates/${sel}`).then((r) => {
      setDetail(r.data);
      setDraft({ custom: r.data.fields.filter((f) => f.custom).map(({ key, label, required_to_attempt }) => ({ key, label, required_to_attempt })), rules: r.data.validation_rules.join("\n"), active: r.data.active, note: "" });
    });
  }, [sel]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/templates/${sel}`, {
        custom_fields: draft.custom.filter((c) => c.key.trim()), validation_rules: draft.rules.split("\n"), active: draft.active, change_note: draft.note,
      });
      toast.success(`Saved — ${r.data.display_name} is now v${r.data.version}. New uploads use this version.`);
      loadList();
      setSel(null);
      setTimeout(() => setSel(r.data.template_id), 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the template");
    } finally {
      setSaving(false);
    }
  };

  const setCustom = (i, patch) => setDraft((d) => ({ ...d, custom: d.custom.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));

  return (
    <section className="mt-10 space-y-4" data-testid="template-manager">
      <div className="flex items-center gap-2">
        <Layers className="w-5 h-5 text-[#f59e0b]" />
        <h2 className="font-display font-bold text-lg">Firm Document Templates</h2>
        <span className="text-xs text-gray-500">{list.length} templates · changes create a new version for every account</span>
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-4">
        <div className="bg-gray-950 border border-white/5 rounded-xl p-2 space-y-1 max-h-[560px] overflow-y-auto">
          {list.map((t) => (
            <button key={t.template_id} onClick={() => setSel(t.template_id)} data-testid={`template-item-${t.template_id}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors duration-200 ${sel === t.template_id ? "bg-[#f59e0b]/15 text-white" : "text-gray-300 hover:bg-white/5"}`}>
              <div className="font-semibold flex items-center gap-2">{t.display_name}{!t.active && <span className="text-[9px] text-rose-300 font-mono">INACTIVE</span>}</div>
              <div className="text-[10px] font-mono text-gray-500">{t.template_id} · v{t.version} · {t.fields.length} fields</div>
            </button>
          ))}
          <NewTemplateForm onCreated={(t) => { loadList(); setSel(t.template_id); }} />
        </div>
        <div className="bg-gray-950 border border-white/5 rounded-xl p-5 min-h-[300px]">
          {!detail || !draft ? <div className="text-xs text-gray-500 italic" data-testid="template-empty">Select a template to view fields, add custom firm fields or edit validation rules.</div> : (
            <div className="space-y-5" data-testid="template-editor">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-bold text-white">{detail.display_name}</h3>
                <span className="text-[10px] font-mono text-gray-400">v{detail.version} · updated {detail.updated_at?.slice(0, 10)} by {detail.updated_by}</span>
                <label className="ml-auto flex items-center gap-2 text-xs text-gray-300">
                  <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} data-testid="template-active-toggle" /> Active
                </label>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1.5">Standard fields</div>
                <div className="flex flex-wrap gap-1.5">{detail.fields.filter((f) => !f.custom).map((f) => <span key={f.key} className="text-[10px] font-mono text-gray-300 border border-white/10 rounded px-1.5 py-0.5">{f.key}</span>)}</div>
              </div>
              <div className="space-y-2">
                <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Custom firm fields</div>
                {draft.custom.map((c, i) => (
                  <div key={i} className="flex flex-wrap gap-2 items-center" data-testid={`custom-field-row-${i}`}>
                    <input value={c.key} onChange={(e) => setCustom(i, { key: e.target.value })} placeholder="field_key" className={`${inp} w-40 font-mono`} data-testid={`custom-field-key-${i}`} />
                    <input value={c.label} onChange={(e) => setCustom(i, { label: e.target.value })} placeholder="Label shown to reviewers" className={`${inp} flex-1 min-w-[160px]`} data-testid={`custom-field-label-${i}`} />
                    <label className="text-[10px] text-gray-400 flex items-center gap-1"><input type="checkbox" checked={c.required_to_attempt} onChange={(e) => setCustom(i, { required_to_attempt: e.target.checked })} /> required</label>
                    <button onClick={() => setDraft((d) => ({ ...d, custom: d.custom.filter((_, j) => j !== i) }))} data-testid={`custom-field-remove-${i}`} className="text-gray-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <button onClick={() => setDraft((d) => ({ ...d, custom: [...d.custom, { key: "", label: "", required_to_attempt: true }] }))} data-testid="custom-field-add-btn" className="flex items-center gap-1 text-[11px] font-mono text-[#f59e0b] hover:text-[#fbbf24]"><Plus className="w-3 h-3" /> Add field</button>
              </div>
              <div className="space-y-1.5">
                <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Validation rules (one per line — sent to the extractor)</div>
                <textarea value={draft.rules} onChange={(e) => setDraft({ ...draft, rules: e.target.value })} rows={6} className={`${inp} w-full leading-relaxed`} data-testid="template-rules-input" />
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="Change note (for the version history)" className={`${inp} flex-1 min-w-[200px]`} data-testid="template-change-note" />
                <button disabled={saving} onClick={save} data-testid="template-save-btn" className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-lg bg-[#f59e0b] text-gray-950 hover:bg-[#fbbf24] disabled:opacity-50"><Save className="w-3.5 h-3.5" /> Save new version</button>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">Version history</div>
                {detail.history.map((h, i) => (
                  <div key={i} className="text-[10px] font-mono text-gray-400 border-t border-white/5 py-1" data-testid={`template-history-${i}`}>v{h.version} · {h.changed_at?.slice(0, 16).replace("T", " ")} · {h.changed_by} · {h.change_note}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
